import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Settings, Plus, Edit, Trash2, CheckCircle, XCircle, AlertTriangle, Upload } from 'lucide-react';
import { QuotaCreateDialog } from './components/QuotaCreateDialog';
import { QuotaEditDialog } from './components/QuotaEditDialog';
import { QuotaDeleteDialog } from './components/QuotaDeleteDialog';
import { QuotaCSVImportDialog } from './components/QuotaCSVImportDialog';
import { QuotaFiltersBar, EMPTY_QUOTA_FILTERS } from './components/QuotaFiltersBar';
import { DataTable } from '../../components/DataTable';
import { AdminQuotasSkeleton } from './components/AdminQuotasSkeleton';
import { Skeleton } from '../../components/ui/skeleton';
import AppContext from '../../contexts/AppContext';
import { hasUserPermission, PERMISSIONS } from '../../utils/permissionUtils';
import { quotaService } from '../../services/quotaService';
import { useServiceDesksWithRequestTypes } from '../../hooks/useServiceDesksWithRequestTypes';
const AdminQuotas = () => {
  const { t } = useTranslation();
  const { currentUser } = useContext(AppContext);
  const canManageQuotas = hasUserPermission(currentUser, PERMISSIONS.DASHBOARD_QUOTAS_MANAGE);
  const [quotas, setQuotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedQuota, setSelectedQuota] = useState(null);
  const [quotaCounts, setQuotaCounts] = useState([]);
  const [countsLoading, setCountsLoading] = useState(true);
  const [exceededModalOpen, setExceededModalOpen] = useState(false);
  const exceededQuotas = useMemo(() => quotaCounts.filter(q => q.exceeded), [quotaCounts]);
  // The rules freeze the request type's name in their JSONB; a Jira rename never reaches them.
  // The id is the stable key, so the live name is resolved from it and the stored string is
  // only a fallback. The endpoint needs DASHBOARD_QUOTAS_MANAGE, hence the guard.
  const { serviceDesks: desksWithRequestTypes } = useServiceDesksWithRequestTypes(canManageQuotas);

  const requestTypeNames = useMemo(() => {
    const names = new Map();
    desksWithRequestTypes.forEach((sd) =>
      sd.requestTypes?.forEach((rt) => names.set(String(rt.id), rt.name))
    );
    return names;
  }, [desksWithRequestTypes]);

  const requestTypeLabel = useCallback(
    (quota) => requestTypeNames.get(String(quota?.scope?.requestTypeId)) || quota?.filters?.requestType || '',
    [requestTypeNames]
  );

  const [filters, setFilters] = useState(EMPTY_QUOTA_FILTERS);

  const [dialogState, setDialogState] = useState({
    create: false,
    edit: false,
    delete: false,
    import: false
  });

  const loadQuotaCounts = async () => {
    try {
      setCountsLoading(true);
      const data = await quotaService.getCounts();
      setQuotaCounts(data);
    } catch (err) {
      console.error('Failed to load quota counts:', err);
    } finally {
      setCountsLoading(false);
    }
  };

  const loadQuotas = async () => {
    try {
      setLoading(true);
      const minDelay = new Promise(resolve => setTimeout(resolve, 600));
      const [response] = await Promise.all([fetchWithAuth('/admin/quotas'), minDelay]);

      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }

      const json = await response.json();

      if (json.success) {
        setQuotas(json.data);
      } else {
        throw new Error(json.error || 'Failed to fetch quotas');
      }
    } catch (err) {
      console.error('Failed to load quotas:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuotas();
    loadQuotaCounts();
  }, []);

  const handleCreateQuota = () => {
    setDialogState({ create: true, edit: false, delete: false, import: false });
  };

  const handleImportQuotas = () => {
    setDialogState({ create: false, edit: false, delete: false, import: true });
  };

  const handleEditQuota = (quota) => {
    setSelectedQuota(quota);
    setDialogState({ create: false, edit: true, delete: false, import: false });
  };

  const handleDeleteQuota = (quota) => {
    setSelectedQuota(quota);
    setDialogState({ create: false, edit: false, delete: true, import: false });
  };

  const handleCloseDialogs = () => {
    setDialogState({ create: false, edit: false, delete: false, import: false });
    setSelectedQuota(null);
  };

  const handleQuotaCreated = () => {
    loadQuotas();
    handleCloseDialogs();
  };

  const handleQuotaUpdated = () => {
    loadQuotas();
    handleCloseDialogs();
  };

  const handleQuotaDeleted = () => {
    loadQuotas();
    handleCloseDialogs();
  };

  // Extract a custom field value from quota filters
  const getFilterValue = (quota, fieldKey) => {
    const field = quota?.filters?.customFields?.find(f => f.fieldKey === fieldKey);
    if (!field) return null;
    return Array.isArray(field.value) ? field.value.join(', ') : field.value;
  };

  // Format window type for display
  const formatWindowType = (window) => {
    if (!window) return '-';
    switch (window.type) {
      case 'month':
        return t('Monthly');
      case 'isoQuarter':
        return t('Quarterly');
      case 'rollingDays':
        return t('{{count}} rolling days', { count: window.days });
      case 'fixed':
        return t('Fixed ({{from}} - {{to}})', { from: window.from, to: window.to });
      case 'none':
        return t('No limit');
      default:
        return window.type;
    }
  };

  // Table columns definition
  const columns = useMemo(
    () => [
      {
        accessorFn: (row) => row.enabled ? t('Active') : t('Inactive'),
        id: "enabled",
        header: t('Status'),
        enableSorting: true,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {row.original.enabled ? (
              <>
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-green-600 text-sm font-medium">{t('Active')}</span>
              </>
            ) : (
              <>
                <XCircle className="w-4 h-4 text-gray-400" />
                <span className="text-gray-400 text-sm">{t('Inactive')}</span>
              </>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'serviceDeskName',
        header: t('Service Desk'),
        enableSorting: true,
        filterFn: 'equalsString',
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium">{row.original.serviceDeskName}</span>
            <span className="text-xs text-gray-500">ID: {row.original.scope.serviceDeskId}</span>
          </div>
        ),
      },
      {
        accessorFn: (row) => requestTypeLabel(row),
        id: 'requestType',
        header: t('Request type'),
        enableSorting: true,
        filterFn: 'equalsString',
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="text-sm">{requestTypeLabel(row.original) || '-'}</span>
            <span className="text-xs text-gray-400">ID: {row.original.scope.requestTypeId}</span>
          </div>
        ),
      },
      {
        accessorFn: (row) => getFilterValue(row, 'brand') || '',
        id: 'brand',
        header: t('Brand'),
        enableSorting: true,
        cell: ({ row }) => {
          const brand = getFilterValue(row.original, 'brand');
          return brand
            ? <Badge variant="outline" className="text-xs">{brand}</Badge>
            : <span className="text-gray-400 text-sm">-</span>;
        },
      },
      {
        accessorFn: (row) => getFilterValue(row, 'sector') || '',
        id: 'sector',
        header: t('Sector'),
        enableSorting: true,
        cell: ({ row }) => {
          const sector = getFilterValue(row.original, 'sector');
          return sector
            ? <Badge variant="secondary" className="text-xs">{sector}</Badge>
            : <span className="text-gray-400 text-sm">-</span>;
        },
      },
      {
        accessorFn: (row) => formatWindowType(row.window),
        id: 'window',
        header: t('Period'),
        enableSorting: true,
        cell: ({ row }) => {
          return <span className="text-sm">{formatWindowType(row.original.window)}</span>
        },
      },
      {
        accessorKey: 'limit',
        header: t('Limit'),
        enableSorting: true,
        cell: ({ row }) => (
          <span className="font-semibold text-sm">{row.original.limit}</span>
        ),
      },
      {
        accessorFn: (row) => {
          const quota = quotaCounts.find(eq => eq.id === row.id);
          return quota ? quota.count : 0;
        },
        id: 'exceeded',
        header: t('Reached'),
        enableSorting: true,
        cell: ({ row }) => {
          const quota = quotaCounts.find(eq => eq.id === row.original.id);
          if (!quota || quota.count === 0) {
            return (
              <div className="flex items-center gap-1.5">
                <span className="text-gray-500 text-sm font-medium">{t('No quota')}</span>
              </div>
            );
          }
          const percentage = Math.min((quota.count / quota.limit) * 100, 100);
          const isExceeded = quota.exceeded;
          return (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1.5">
                {isExceeded
                  ? <AlertTriangle className={`w-4 h-4 ${quota.behavior === 'BLOCK' ? 'text-red-500' : 'text-orange-500'}`} />
                  : <CheckCircle className="w-4 h-4 text-green-500" />
                }
                <span className={`text-sm font-semibold ${isExceeded ? (quota.behavior === 'BLOCK' ? 'text-red-600' : 'text-orange-600') : 'text-gray-700'}`}>
                  {quota.count} / {quota.limit}
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full ${isExceeded ? (quota.behavior === 'BLOCK' ? 'bg-red-500' : 'bg-orange-400') : 'bg-green-500'}`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          );
        },
      },
      {
        accessorFn: (row) => row?.onExceed?.behavior === 'BLOCK' ? t('Block') : t('Warn'),
        id: 'onExceed.behavior',
        header: t('Action'),
        enableSorting: true,
        cell: ({ row }) => (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            row.original.onExceed.behavior === 'BLOCK'
              ? 'bg-red-100 text-red-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}>
            {row.original.onExceed.behavior === 'BLOCK' ? t('Block') : t('Warn')}
          </span>
        ),
      },
      ...(canManageQuotas ? [{
        id: 'actions',
        header: t('Action'),
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex gap-2">
            <button
              onClick={() => handleEditQuota(row.original)}
              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
              title={t('Edit')}
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDeleteQuota(row.original)}
              className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
              title={t('Delete')}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ),
      }] : []),
    ],
    [canManageQuotas, quotaCounts, requestTypeLabel, t]
  );

  const uniqueSectors = useMemo(() => {
    const sectorsMap = new Map();
    quotas.forEach(quota => {
      const sectorField = quota?.filters?.customFields?.find(field => field.fieldKey === 'sector');
      if (!sectorField) {
        return;
      }
      if (!sectorsMap.has(sectorField.value)) {
        sectorsMap.set(sectorField.value, {
          id: sectorField.value,
          name: sectorField.value
        });
      }
    });
    return Array.from(sectorsMap.values());
  }, [quotas.length]);

  // Keyed by id, never by label: the request type's name is resolved asynchronously, so a label
  // picked before it lands would stop matching the moment the live names replace the stored ones.
  const uniqueServiceDesks = useMemo(() => {
    const desks = new Map();
    quotas.forEach((quota) => {
      const value = String(quota.scope?.serviceDeskId ?? '');
      if (value && !desks.has(value)) desks.set(value, { value, label: quota.serviceDeskName || value });
    });
    return Array.from(desks.values());
  }, [quotas]);

  const uniqueRequestTypes = useMemo(() => {
    const types = new Map();
    quotas.forEach((quota) => {
      const value = String(quota.scope?.requestTypeId ?? '');
      if (value && !types.has(value)) types.set(value, { value, label: requestTypeLabel(quota) || value });
    });
    return Array.from(types.values());
  }, [quotas, requestTypeLabel]);

  const uniqueBrands = useMemo(() => {
    const brandsMap = new Map();
    quotas.forEach(quota => {
      const brandField = quota?.filters?.customFields?.find(field => field.fieldKey === 'brand');
      if (!brandField) {
        return;
      }
      const values = Array.isArray(brandField.value) ? brandField.value : [brandField.value];
      values.forEach(value => {
        if (!brandsMap.has(value)) {
          brandsMap.set(value, {
            id: value,
            name: value
          });
        }
      });
    });
    return Array.from(brandsMap.values());
  }, [quotas.length]);

  const filteredQuotas = useMemo(
    () =>
      quotas.filter((quota) =>
        (filters.serviceDesk === 'all' || String(quota.scope?.serviceDeskId) === filters.serviceDesk) &&
        (filters.requestType === 'all' || String(quota.scope?.requestTypeId) === filters.requestType) &&
        (filters.sector === 'all' || getFilterValue(quota, 'sector') === filters.sector) &&
        (filters.brand === 'all' || getFilterValue(quota, 'brand') === filters.brand)
      ),
    [quotas, filters]
  );

  // Calculate statistics
  const stats = {
    total: filteredQuotas.length,
    active: filteredQuotas.filter(q => q.enabled).length,
    inactive: filteredQuotas.filter(q => !q.enabled).length,
    blocking: filteredQuotas.filter(q => q.onExceed?.behavior === 'BLOCK').length
  };

  if (loading && quotas.length === 0) {
    return <AdminQuotasSkeleton />;
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
        <h3 className="font-bold">{t('An error occurred')}</h3>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-4">
      {/* The spelling the other admin screens give their header — see `AdminDeployments`,
          `AdminIssues` and `AdminGroups`: the shared Card, the icon in `text-primary`, the
          CardTitle, and the actions on the right of the same row. */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Settings className="w-6 h-6 text-primary" />
              <div>
                <CardTitle>{t('Quotas')}</CardTitle>
                <p className="text-sm text-gray-500">{t('Create, edit or delete quota rules')}</p>
              </div>
            </div>
            {canManageQuotas && (
              <div className="flex items-center gap-2">
                <Button onClick={handleImportQuotas} variant="outline">
                  <Upload className="w-4 h-4 mr-2" />
                  {t('Import a CSV')}
                </Button>
                <Button onClick={handleCreateQuota}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('Create a quota')}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('Total Quotas')}</CardTitle>
            <Settings className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('All configured rules')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('Enabled quotas')}</CardTitle>
            <Badge className="h-4 px-2 bg-green-100 text-green-800">{t('Active')}</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.active}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('Quotas being enforced')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('Disabled quotas')}</CardTitle>
            <Badge variant="secondary" className="h-4 px-2">{t('Inactive')}</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.inactive}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('Quotas disabled')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('Blocking quotas')}</CardTitle>
            <Badge variant="destructive" className="h-4 px-2">{t('Block')}</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.blocking}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('Quotas with blocking')}
            </p>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-colors hover:border-orange-300 ${
            exceededQuotas.length > 0 ? 'border-orange-400 bg-orange-50/50' : ''
          }`}
          onClick={() => setExceededModalOpen(true)}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('Quotas reached')}</CardTitle>
            <AlertTriangle className={`h-4 w-4 ${exceededQuotas.length > 0 ? 'text-orange-500' : 'text-muted-foreground'}`} />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${exceededQuotas.length > 0 ? 'text-orange-600' : ''}`}>
              {countsLoading ? <Skeleton className="h-8 w-8" /> : exceededQuotas.length}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {t('Quotas currently exceeded')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quotas Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t('Configured quota rules')}</CardTitle>
          <CardDescription>
            {t('View, create, edit or delete quota rules')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <QuotaFiltersBar
            filters={filters}
            onChange={(next) => setFilters((prev) => ({ ...prev, ...next }))}
            onReset={() => setFilters(EMPTY_QUOTA_FILTERS)}
            options={{
              serviceDesks: uniqueServiceDesks,
              requestTypes: uniqueRequestTypes,
              sectors: uniqueSectors.map((sector) => ({ value: sector.name, label: sector.name })),
              brands: uniqueBrands.map((brand) => ({ value: brand.name, label: brand.name })),
            }}
          />

          <DataTable
            data={filteredQuotas}
            columns={columns}
            emptyMessage={t('No quota configured')}
            showGlobalSearch={true}
            showPagination={true}
            pageSize={50}
          />
        </CardContent>
      </Card>

      {/* Dialogs */}
      <QuotaCreateDialog
        open={dialogState.create}
        onClose={handleCloseDialogs}
        onQuotaCreated={handleQuotaCreated}
      />

      <QuotaEditDialog
        open={dialogState.edit}
        quota={selectedQuota}
        onClose={handleCloseDialogs}
        onQuotaUpdated={handleQuotaUpdated}
      />

      <QuotaDeleteDialog
        open={dialogState.delete}
        quota={selectedQuota}
        onClose={handleCloseDialogs}
        onQuotaDeleted={handleQuotaDeleted}
      />

      <QuotaCSVImportDialog
        open={dialogState.import}
        onClose={handleCloseDialogs}
        // Refresh only: the dialog is on its completion step and closes itself on the user's
        // click. Closing it here would destroy the recap of what was just written.
        onImported={loadQuotas}
      />

      {/* Exceeded Quotas Modal */}
      <Dialog open={exceededModalOpen} onOpenChange={setExceededModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              {t('Quotas reached')}
            </DialogTitle>
            <DialogDescription>
              {exceededQuotas.length === 0
                ? t('No quota is currently exceeded.')
                : t('{{count}} quota currently exceeded.', { count: exceededQuotas.length })
              }
            </DialogDescription>
          </DialogHeader>

          {exceededQuotas.length > 0 && (
            <div className="space-y-3 mt-2">
              {exceededQuotas.map((eq) => (
                <div
                  key={eq.id}
                  className="border rounded-lg p-4 space-y-2 bg-white"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">{eq.serviceDeskName}</span>
                      {eq.filters?.requestType && (
                        <span className="text-xs text-gray-500">{eq.filters.requestType}</span>
                      )}
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      eq.behavior === 'BLOCK'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {eq.behavior === 'BLOCK' ? t('Block') : t('Warn')}
                    </span>
                  </div>

                  {/* Brand / Sector badges */}
                  {eq.filters?.customFields?.length > 0 && (
                    <div className="flex items-center gap-2">
                      {eq.filters.customFields.map((f, i) => (
                        <Badge key={i} variant={f.fieldKey === 'brand' ? 'outline' : 'secondary'} className="text-xs">
                          {f.fieldKey === 'brand' ? t('Brand') : f.fieldKey === 'sector' ? t('Sector') : f.fieldKey}: {Array.isArray(f.value) ? f.value.join(', ') : f.value}
                        </Badge>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1.5">
                      <span className="text-gray-500">{t('Counter:')}</span>
                      <span className="font-bold text-orange-600">{eq.count}</span>
                      <span className="text-gray-400">/</span>
                      <span className="font-medium">{eq.limit}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-gray-500">{t('Period:')}</span>
                      <span>{formatWindowType(eq.window)}</span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        eq.behavior === 'BLOCK' ? 'bg-red-500' : 'bg-orange-400'
                      }`}
                      style={{ width: `${Math.min((eq.count / eq.limit) * 100, 100)}%` }}
                    />
                  </div>

                  {eq.window.from && eq.window.to && (
                    <p className="text-xs text-gray-400">
                      {t('Window:')} {eq.window.from} — {eq.window.to}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminQuotas;
